import { Card, Container, Link, Text, TextField } from "@radix-ui/themes";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import { usePostHog } from "../../../hooks/usePostHog";
import { recentInstallationChoice } from "../../../utils/installationChoice";
import { announce } from "../../onboarding/setupChannel";
import { composerLink, FIRST_PROMPT, rememberedClient } from "../../onboarding/setupCopy";
import "../../onboarding/remoteSetup.css";
import Footer from "../../shared/Footer";

interface DeviceVerificationViewProps {
    session: any;
    userCode?: string;
    onComplete: (data?: { deviceName?: string; email?: string }) => void;
}

export default function DeviceVerificationView({ session, userCode: initialCode, onComplete }: DeviceVerificationViewProps) {
    const { track, trackOnce } = usePostHog();
    const { signOut } = useAuth();
    // Seed from the URL code when present; stays editable so a codeless visit
    // (bare verification_uri, reopened/expired link) can still type the code.
    const [userCode, setUserCode] = useState(initialCode || "");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);
    const [deviceName, setDeviceName] = useState("");
    // The setup tab stored which assistant this run came from; same origin, so we can read it here.
    const [client] = useState(() => rememberedClient());
    const [retryCount, setRetryCount] = useState(0);
    const hasTrackedPageView = useRef(false);

    // Tell the setup tab the command ran and this page is open, so it can move to its Verify card.
    useEffect(() => {
        announce("verify_opened");
    }, []);

    // Track page view on mount (only once)
    useEffect(() => {
        if (session?.user && !hasTrackedPageView.current) {
            hasTrackedPageView.current = true;
            trackOnce("page_device_verification_viewed", initialCode || session.user.id, {
                user_id: session.user.id,
                email: session.user.email,
                has_user_code: !!initialCode,
                // "unknown" when the setup tab was a different browser, or the client was unrecognised.
                client: client ?? "unknown",
            });
        }
    }, [
        session?.user,
        trackOnce,
        initialCode,
        client,
    ]);

    const handleVerify = useCallback(async () => {
        if (!userCode) {
            return;
        }
        setLoading(true);
        setError("");

        track("device_connection_started", {
            user_id: session?.user?.id,
            retry_count: retryCount,
        });

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000); // 30s timeout

        try {
            const response = await fetch("/device/verify", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${session.access_token}`,
                },
                body: JSON.stringify({
                    user_code: userCode.toUpperCase().trim(),
                }),
                signal: controller.signal,
            });
            clearTimeout(timeoutId);

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error_description || "Verification failed");
            }

            const data = await response.json();
            setSuccess(true);
            setDeviceName(data.device_name || "");

            // Best-effort same-browser attribution; never claim device-side proof of method.
            const installChoice = recentInstallationChoice(session.user.id);
            track("device_verification_completed", {
                user_id: session.user.id,
                email: session.user.email,
                device_name: data.device_name || "unknown",
                device_os: navigator.platform || "unknown",
                retry_count: retryCount,
                client: client ?? "unknown",
                ...(installChoice
                    ? {
                          installation_method: installChoice.method,
                          installation_os: installChoice.os,
                          installation_attribution: "recent_same_browser_choice",
                      }
                    : {}),
            });

            // Verified — hand back to the page, which closes this tab so the user
            // returns to the setup page they came from.
            setTimeout(() => {
                onComplete({
                    deviceName: data.device_name,
                    email: session.user.email,
                });
            }, 1200);
        } catch (err: any) {
            setError(err.message);

            if (err.name === "AbortError") {
                track("device_connection_timeout", {
                    user_id: session?.user?.id,
                    email: session?.user?.email,
                    retry_count: retryCount,
                });
                setError("Connection timed out. Please try again.");
                return;
            }

            // Track verification failure
            track("device_verification_failed", {
                user_id: session?.user?.id,
                email: session?.user?.email,
                error_message: err.message,
                retry_count: retryCount,
                device_os: navigator.platform || "unknown",
            });
            setRetryCount((prev) => prev + 1);
        } finally {
            setLoading(false);
        }
    }, [
        userCode,
        session,
        onComplete,
        track,
        retryCount,
        client,
    ]);

    return (
        <Container
            size="2"
            p="4"
            style={{
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                // Optical centering — consistent with the other auth-flow screens.
                paddingBottom: "12vh",
            }}
        >
            <Card
                size="4"
                style={{
                    width: "100%",
                    maxWidth: 560,
                    margin: "0 auto",
                }}
            >
                <div className="rs-root">
                    {success && <div className="rs-check">✓</div>}
                    <h1 className="rs-h1">{success ? "You’re connected" : "Verify this device?"}</h1>
                    <p className="rs-lede">
                        {success
                            ? `This device now works with ${client ?? "your AI chat"}.`
                            : initialCode
                              ? "Check this code matches the one where you ran the command."
                              : "Enter the code shown where you ran the command."}
                    </p>
                </div>

                {!success && (
                    <>
                        {/* No code in the URL (bare verification_uri, reopened or expired
                link): let the user type the code shown on their device. */}
                        {!initialCode && (
                            <TextField.Root
                                size="3"
                                placeholder="XXXX-XXXX"
                                value={userCode}
                                onChange={(e) => {
                                    const val = e.target.value
                                        .replace(/[^A-Za-z0-9]/g, "")
                                        .toUpperCase()
                                        .slice(0, 8);
                                    setUserCode(val.length > 4 ? `${val.slice(0, 4)}-${val.slice(4)}` : val);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && userCode.length >= 8 && !loading) {
                                        handleVerify();
                                    }
                                }}
                                disabled={loading}
                                style={{
                                    fontSize: "1.5rem",
                                    fontFamily: "monospace",
                                    textAlign: "center",
                                    letterSpacing: "0.1em",
                                }}
                            />
                        )}

                        {/* The code is the point of this screen: shown as something to check, not
                            as a receipt at the bottom. */}
                        {initialCode && (
                            <div className="rs-root">
                                <span className="rs-code">{userCode}</span>
                            </div>
                        )}

                        {error && <p className="rs-error">{error}</p>}

                        <button
                            type="button"
                            className="rs-primary"
                            style={{
                                marginTop: 20,
                            }}
                            onClick={handleVerify}
                            disabled={loading || userCode.length < 8}
                        >
                            {loading ? "Verifying…" : "Verify device"}
                        </button>

                        <div
                            style={{
                                marginTop: 16,
                                textAlign: "center",
                            }}
                        >
                            <Text size="1" color="gray">
                                Signed in as <strong>{session?.user?.email}</strong> ·{" "}
                                <Link
                                    href="#"
                                    onClick={async (e) => {
                                        e.preventDefault();
                                        await signOut();
                                        const searchParams = new URLSearchParams(window.location.search);
                                        window.location.href = `/?${searchParams.toString()}`;
                                    }}
                                    style={{
                                        cursor: "pointer",
                                    }}
                                >
                                    Sign out
                                </Link>
                            </Text>
                        </div>
                    </>
                )}

                {/* The finish line. The user is already looking at this tab, so it ends here: it
                    says it worked, names the assistant, and hands over something to try — rather
                    than sending them to a dashboard, which is not what they came for. */}
                {success && (
                    <div className="rs-root">
                        {deviceName && (
                            <div className="rs-device">
                                <i />
                                {deviceName} <em>online</em>
                            </div>
                        )}

                        <div className="rs-promptlabel">A safe first thing to try — read-only:</div>
                        <div className="rs-prompt">{FIRST_PROMPT}</div>

                        {client ? (
                            <a
                                className="rs-primary"
                                href={composerLink(client, FIRST_PROMPT)}
                                target="_blank"
                                rel="noopener"
                                data-testid="finish"
                                onClick={() =>
                                    void track("onboarding_finish_clicked", {
                                        user_id: session?.user?.id,
                                        client,
                                        via: "composer_link",
                                    })
                                }
                            >
                                Try this prompt in {client}
                            </a>
                        ) : (
                            <button
                                type="button"
                                className="rs-primary"
                                data-testid="finish"
                                onClick={() => {
                                    void navigator.clipboard?.writeText(FIRST_PROMPT).catch(() => undefined);
                                    void track("onboarding_finish_clicked", {
                                        user_id: session?.user?.id,
                                        client: "unknown",
                                        via: "copy_prompt",
                                    });
                                }}
                            >
                                Copy this prompt
                            </button>
                        )}

                        <p className="rs-hint">
                            You can minimize the window where you ran the command. Keep Desktop Commander Remote running to stay connected.
                        </p>
                        {/* The other way out of this screen. Untracked, it looks like a drop-off. */}
                        <a
                            className="rs-mutedlink"
                            href="/"
                            onClick={() =>
                                void track("onboarding_finish_clicked", {
                                    user_id: session?.user?.id,
                                    client: client ?? "unknown",
                                    via: "manage_devices",
                                })
                            }
                        >
                            Manage devices
                        </a>
                    </div>
                )}
            </Card>
            <Footer />
        </Container>
    );
}
